import React, { useEffect, useState } from "react";
import { Menu, X, LogIn } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/site/Logo";
import { trackEvent } from "@/lib/analytics";

const links = [
  { id: "filosofia", label: "O Lu.ft" },
  { id: "aulas", label: "Aulas" },
  { id: "strength", label: "Strength" },
  { id: "biblioteca", label: "Biblioteca" },
  { id: "equipa", label: "Equipa" },
  { id: "estudios", label: "Estúdios" },
  { id: "contactos", label: "Contactos" },
];

const YOGO_PROFILE_URL = "https://luftpilates.yogobooking.pt/frontend/index.html#/my-profile";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "backdrop-blur-xl bg-[rgba(246,244,238,0.78)] border-b border-[var(--luft-line)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navigation"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between h-20 sm:h-24">
        <button
          onClick={goHome}
          className="flex items-center"
          data-testid="nav-logo-button"
          aria-label="Lu.ft início"
        >
          <Logo />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6" data-testid="nav-desktop-links">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleNav(l.id)}
              className="link-underline text-[0.72rem] uppercase tracking-[0.28em] text-[var(--luft-ink)] hover:text-[var(--luft-blue)] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href={YOGO_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.28em] text-[var(--luft-ink)] link-underline hover:text-[var(--luft-blue)] transition-colors"
            data-testid="nav-entrar-btn"
          >
            <LogIn size={13} strokeWidth={1.4} />
            Entrar
          </a>
          <button
            onClick={() => {
              trackEvent("reservar_click", { cta_location: "nav" });
              setOpen(false);
              navigate("/reservar");
            }}
            className="btn-ink"
            data-testid="nav-reservar-btn"
          >
            Reservar
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 -mr-2 text-[var(--luft-ink)]"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
        </button>
      </div>

      {/* Mobile panel */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        } bg-[var(--luft-bg)] border-t border-[var(--luft-line)]`}
        data-testid="nav-mobile-panel"
      >
        <div className="px-6 py-8 flex flex-col gap-6">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleNav(l.id)}
              className="text-left font-display text-3xl text-[var(--luft-ink)]"
              data-testid={`nav-mobile-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => {
              trackEvent("reservar_click", { cta_location: "nav_mobile" });
              setOpen(false);
              navigate("/reservar");
            }}
            className="btn-ink mt-2 self-start"
            data-testid="nav-mobile-reservar-btn"
          >
            Reservar
          </button>

          <a
            href={YOGO_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[var(--luft-ink)] text-sm font-body link-underline self-start"
            data-testid="nav-mobile-entrar-btn"
          >
            <LogIn size={14} strokeWidth={1.4} />
            Entrar · área pessoal
          </a>

          <div className="pt-6 mt-2 border-t border-[var(--luft-line)]">
            <p className="eyebrow mb-4">App Lu.ft</p>
            <div className="flex flex-col gap-2">
              <a
                href="https://apps.apple.com/pt/app/luft-pilates-studio/id6761007459"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline text-[var(--luft-ink)] font-body text-sm self-start"
                data-testid="nav-mobile-app-store"
              >
                Descarregar para iOS
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=pt.yogobooking.luftpilates&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline text-[var(--luft-ink)] font-body text-sm self-start"
                data-testid="nav-mobile-google-play"
              >
                Descarregar para Android
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
